/**
 * 12-tier producer definitions
 * Headless data - no GUI
 */

export interface ProducerDef {
  id: string;
  nameKey: string; // i18n key
  descKey: string;
  baseCost: number;
  baseCps: number; // base CPS (clicks per second -> IP per second)
  unlockCondition: ProducerUnlockCondition;
}

export type ProducerUnlockCondition =
  | { type: 'start' }
  | { type: 'prevProducer'; producerId: string }
  | { type: 'prestige'; count: number };

export const PRODUCERS: ProducerDef[] = [
  { id: 'informant', nameKey: 'prod_informant', descKey: 'prod_informant_desc', baseCost: 15, baseCps: 0.5, unlockCondition: { type: 'start' } },
  { id: 'hacker', nameKey: 'prod_hacker', descKey: 'prod_hacker_desc', baseCost: 100, baseCps: 1, unlockCondition: { type: 'prevProducer', producerId: 'informant' } },
  { id: 'disguiser', nameKey: 'prod_disguiser', descKey: 'prod_disguiser_desc', baseCost: 500, baseCps: 5, unlockCondition: { type: 'prevProducer', producerId: 'hacker' } },
  { id: 'mindController', nameKey: 'prod_mindController', descKey: 'prod_mindController_desc', baseCost: 3000, baseCps: 15, unlockCondition: { type: 'prevProducer', producerId: 'disguiser' } },
  { id: 'secretLab', nameKey: 'prod_secretLab', descKey: 'prod_secretLab_desc', baseCost: 10000, baseCps: 50, unlockCondition: { type: 'prevProducer', producerId: 'mindController' } },
  { id: 'mediaCell', nameKey: 'prod_mediaCell', descKey: 'prod_mediaCell_desc', baseCost: 40000, baseCps: 150, unlockCondition: { type: 'prevProducer', producerId: 'secretLab' } },
  { id: 'shadowGovt', nameKey: 'prod_shadowGovt', descKey: 'prod_shadowGovt_desc', baseCost: 200000, baseCps: 500, unlockCondition: { type: 'prevProducer', producerId: 'mediaCell' } },
  { id: 'deepBase', nameKey: 'prod_deepBase', descKey: 'prod_deepBase_desc', baseCost: 1666666, baseCps: 2000, unlockCondition: { type: 'prevProducer', producerId: 'shadowGovt' } },
  { id: 'orbitalStation', nameKey: 'prod_orbitalStation', descKey: 'prod_orbitalStation_desc', baseCost: 123456789, baseCps: 10000, unlockCondition: { type: 'prevProducer', producerId: 'deepBase' } },
  { id: 'dimensionGate', nameKey: 'prod_dimensionGate', descKey: 'prod_dimensionGate_desc', baseCost: 3999999999, baseCps: 150000, unlockCondition: { type: 'prestige', count: 1 } },
  { id: 'planetController', nameKey: 'prod_planetController', descKey: 'prod_planetController_desc', baseCost: 75000000000, baseCps: 1000000, unlockCondition: { type: 'prestige', count: 3 } },
  { id: 'galacticBeacon', nameKey: 'prod_galacticBeacon', descKey: 'prod_galacticBeacon_desc', baseCost: 10000000000000, baseCps: 15000000, unlockCondition: { type: 'prestige', count: 10 } },
];

/** Cost formula: baseCost * 1.15^n */
export function getProducerCost(baseCost: number, owned: number, discountPercent: number = 0): number {
  const raw = baseCost * Math.pow(1.15, owned);
  return Math.floor(raw * (1 - discountPercent / 100));
}

/** Milestone multipliers by owned count */
export interface Milestone {
  count: number;
  multiplier: number;
  nameKey: string;
}

export const MILESTONES: Milestone[] = [
  { count: 10, multiplier: 1.5, nameKey: 'milestone_squad' },
  { count: 25, multiplier: 2, nameKey: 'milestone_company' },
  { count: 50, multiplier: 3, nameKey: 'milestone_battalion' },
  { count: 100, multiplier: 4, nameKey: 'milestone_regiment' },
  { count: 150, multiplier: 8, nameKey: 'milestone_division' },
  { count: 200, multiplier: 16, nameKey: 'milestone_corps' },
];

/** Get total milestone multiplier for a given count */
export function getMilestoneMultiplier(count: number): number {
  let mult = 1;
  for (const m of MILESTONES) {
    if (count >= m.count) {
      mult = m.multiplier;
    }
  }
  return mult;
}

export function getProducerById(id: string): ProducerDef | undefined {
  return PRODUCERS.find(p => p.id === id);
}
